export type EegChannelCatalogEntry = {
  channel_code: string;
  electrode_system?: string | null;
  hemisphere?: string | null;
  region?: string | null;
  position_x?: number | null;
  position_y?: number | null;
  position_z?: number | null;
  display_order?: number | null;
  is_active?: boolean | null;
};

type HeadMapRegion =
  | "frontal"
  | "central"
  | "temporal"
  | "parietal"
  | "occipital"
  | "reference";

export type HeadMapElectrode = {
  name: string;
  x: number; // 0-100
  y: number; // 0-100 (top=anterior)
  region: HeadMapRegion;
};

export type EegChannelPairSuggestion = {
  positive: string;
  negative: string;
  label: string;
};

const catalogByCode = new Map<string, EegChannelCatalogEntry>();
const loadedSystemKeys = new Set<string>();
const inFlightBySystem = new Map<string, Promise<void>>();

const normalizeCode = (value: string): string => String(value || "").trim().toUpperCase();

const toSystemKey = (system?: string): string =>
  String(system || "*").trim().toUpperCase();

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

const toHeadMapRegion = (region: string | null | undefined): HeadMapRegion => {
  const normalized = String(region || "").trim().toLowerCase();
  if (
    normalized === "frontal" ||
    normalized === "central" ||
    normalized === "temporal" ||
    normalized === "parietal" ||
    normalized === "occipital" ||
    normalized === "reference"
  ) {
    return normalized;
  }
  return "reference";
};

export function upsertEegChannelCatalog(entries: EegChannelCatalogEntry[]): void {
  for (const entry of entries) {
    const key = normalizeCode(entry.channel_code);
    if (!key) continue;
    const existing = catalogByCode.get(key);
    const displayCode = String(entry.channel_code || "").trim();
    catalogByCode.set(key, {
      ...existing,
      ...entry,
      channel_code: displayCode || existing?.channel_code || key,
    });
  }
}

export function hasEegChannelCatalogEntries(): boolean {
  return catalogByCode.size > 0;
}

export function isKnownEegChannelCode(code: string): boolean {
  return catalogByCode.has(normalizeCode(code));
}

export function getEegChannelCatalogEntry(
  code: string,
): EegChannelCatalogEntry | null {
  return catalogByCode.get(normalizeCode(code)) || null;
}

export function getEegChannelCatalogEntries(
  system?: string,
): EegChannelCatalogEntry[] {
  const requestedSystem = String(system || "").trim().toUpperCase();
  return Array.from(catalogByCode.values())
    .filter((entry) => entry.is_active !== false)
    .filter((entry) => {
      if (!requestedSystem) return true;
      return (
        String(entry.electrode_system || "").trim().toUpperCase() ===
        requestedSystem
      );
    })
    .sort((a, b) => {
      const ao = isFiniteNumber(a.display_order)
        ? a.display_order
        : Number.MAX_SAFE_INTEGER;
      const bo = isFiniteNumber(b.display_order)
        ? b.display_order
        : Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      return String(a.channel_code).localeCompare(String(b.channel_code));
    });
}

export function getEegChannelCodesFromCatalog(system?: string): string[] {
  return getEegChannelCatalogEntries(system).map((entry) =>
    String(entry.channel_code || "").trim(),
  );
}

export function getEegReferenceElectrodeCodesFromCatalog(
  system?: string,
): string[] {
  const set = new Set<string>();
  for (const entry of getEegChannelCatalogEntries(system)) {
    const code = String(entry.channel_code || "").trim();
    if (!code) continue;
    const normalizedRegion = String(entry.region || "")
      .trim()
      .toLowerCase();
    if (normalizedRegion === "reference") {
      set.add(code);
    }
    if (/^(A\d|M\d)$/i.test(code)) {
      set.add(code);
    }
    if (/^CZ$/i.test(code)) {
      set.add(code);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function buildLongitudinalChainsFromCatalog(system?: string): string[][] {
  const channels = getEegChannelCatalogEntries(system).filter(
    (entry) => String(entry.region || "").trim().toLowerCase() !== "reference",
  );

  const left = channels
    .filter((entry) => String(entry.hemisphere || "").trim().toLowerCase() === "left")
    .map((entry) => String(entry.channel_code || "").trim());
  const right = channels
    .filter(
      (entry) => String(entry.hemisphere || "").trim().toLowerCase() === "right",
    )
    .map((entry) => String(entry.channel_code || "").trim());
  const midline = channels
    .filter(
      (entry) =>
        String(entry.hemisphere || "").trim().toLowerCase() === "midline",
    )
    .map((entry) => String(entry.channel_code || "").trim());

  return [left, right, midline].filter((chain) => chain.length >= 2);
}

export function buildBipolarPairsFromCatalog(
  system?: string,
): EegChannelPairSuggestion[] {
  const pairs: EegChannelPairSuggestion[] = [];
  const seen = new Set<string>();
  const chains = buildLongitudinalChainsFromCatalog(system);

  const pushPair = (positive: string, negative: string) => {
    if (!positive || !negative || positive === negative) return;
    const key = `${normalizeCode(positive)}->${normalizeCode(negative)}`;
    if (seen.has(key)) return;
    seen.add(key);
    pairs.push({
      positive,
      negative,
      label: `${positive}-${negative}`,
    });
  };

  for (const chain of chains) {
    for (let i = 0; i < chain.length - 1; i += 1) {
      pushPair(chain[i], chain[i + 1]);
    }
  }

  if (pairs.length === 0) {
    const codes = getEegChannelCodesFromCatalog(system);
    for (let i = 0; i < codes.length - 1; i += 1) {
      pushPair(codes[i], codes[i + 1]);
    }
  }

  return pairs;
}

export function getEegChannel2DPosition(
  code: string,
): { x: number; y: number } | null {
  const entry = getEegChannelCatalogEntry(code);
  if (!entry) return null;
  if (!isFiniteNumber(entry.position_x) || !isFiniteNumber(entry.position_y)) {
    return null;
  }
  return {
    x: clamp01(entry.position_x),
    // DB stores posterior=0/anterior=1; topomap expects top=anterior.
    y: clamp01(1 - entry.position_y),
  };
}

export function getEegChannel3DPosition(
  code: string,
): [number, number, number] | null {
  const entry = getEegChannelCatalogEntry(code);
  if (!entry) return null;
  if (!isFiniteNumber(entry.position_x) || !isFiniteNumber(entry.position_y)) {
    return null;
  }

  const x = clamp01(entry.position_x) * 2 - 1;
  const y = clamp01(entry.position_y) * 2 - 1;
  const z = isFiniteNumber(entry.position_z)
    ? clamp01(entry.position_z) * 2 - 1
    : 0;

  const norm = Math.sqrt(x * x + y * y + z * z);
  if (!Number.isFinite(norm) || norm <= 1e-6) return null;
  return [x / norm, y / norm, z / norm];
}

export function buildHeadMapElectrodesFromCatalog(
  system?: string,
): HeadMapElectrode[] {
  const entries = getEegChannelCatalogEntries(system).filter(
    (entry) =>
      isFiniteNumber(entry.position_x) && isFiniteNumber(entry.position_y),
  );

  return entries.map((entry) => ({
    name: String(entry.channel_code || "").trim(),
    x: clamp01(entry.position_x as number) * 100,
    y: clamp01(1 - (entry.position_y as number)) * 100,
    region: toHeadMapRegion(entry.region),
  }));
}

export async function ensureEegChannelCatalogLoaded(
  system?: string,
): Promise<void> {
  const key = toSystemKey(system);
  if (loadedSystemKeys.has(key)) return;

  const existing = inFlightBySystem.get(key);
  if (existing) {
    await existing;
    return;
  }

  const loader = (async () => {
    const { default: apiClient } = await import("./apiClient");
    const params = new URLSearchParams();
    if (system) {
      params.append("system", system);
    }
    params.append("is_active", "true");

    const response = await apiClient.get<{
      success?: boolean;
      data?: EegChannelCatalogEntry[];
    }>(`/eeg/channels?${params.toString()}`);

    const channels = Array.isArray(response.data?.data) ? response.data.data : [];
    upsertEegChannelCatalog(channels);
    loadedSystemKeys.add(key);
  })();

  inFlightBySystem.set(key, loader);
  try {
    await loader;
  } finally {
    inFlightBySystem.delete(key);
  }
}
